import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

export default function AccountHome({
  historyCount = 0,
  birthdaysCount = 0,
  subscriptionText = "none",
}) {
  const [latest, setLatest] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(true);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingLatest(true);
        const r = await api.get("account/latest-activity");
        if (!alive) return;
        setLatest(r?.data?.latest || null);
      } catch {
        if (!alive) return;
        setLatest(null);
      } finally {
        if (alive) setLoadingLatest(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingHistory(true);
        const r = await api.get("account/search-history?limit=10&order=desc");
        if (!alive) return;
        setHistory(Array.isArray(r?.data?.items) ? r.data.items : []);
      } catch {
        if (!alive) return;
        setHistory([]);
      } finally {
        if (alive) setLoadingHistory(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const formatPrettyDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.slice(0, 10).split("-");
    if (!y || !m || !d) return iso;
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    const month = dt.toLocaleString("en-US", { month: "long" });
    return `${String(d).padStart(2, "0")} - ${month} - ${y}`;
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const dt = new Date(ts);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const latestDob = useMemo(() => {
    return latest?.birthdate ? String(latest.birthdate).slice(0, 10) : "";
  }, [latest]);

  const latestUrl = useMemo(() => {
    return latest?.result_url || (latestDob ? `/search?dob=${latestDob}` : "/");
  }, [latest, latestDob]);

  const prettyDate = useMemo(() => {
    if (latestDob) return formatPrettyDate(latestDob);
    return latest?.input_text || "";
  }, [latestDob, latest]);

  const prettyTime = useMemo(() => {
    return latest?.created_at ? formatTime(latest.created_at) : "";
  }, [latest]);

  const prettyResult = useMemo(() => {
    return latest?.result_text || "";
  }, [latest]);

  const previousItems = useMemo(() => {
    const latestId = latest?.id;
    const items = Array.isArray(history) ? history : [];
    // remove latest from list if duplicated
    return latestId ? items.filter((x) => x?.id !== latestId) : items;
  }, [history, latest]);

  const renderRow = (item) => {
    const dob = item?.birthdate ? String(item.birthdate).slice(0, 10) : "";
    const url = item?.result_url || (dob ? `/search?dob=${dob}` : "/");
    const dateLabel = dob ? formatPrettyDate(dob) : item?.input_text || "View";
    const timeLabel = item?.created_at ? formatTime(item.created_at) : "";
    const resultLabel = item?.result_text || "";

    return (
      <div key={item?.id || `${dob}-${url}`} style={{ padding: "10px 0" }}>
        <div style={{ fontWeight: 800 }}>
          <Link
            to={url}
            style={{
              fontWeight: 900,
              textDecoration: "underline",
              color: "inherit",
            }}
          >
            {dateLabel}
          </Link>
        </div>

        {timeLabel ? (
          <div style={{ marginTop: 4, opacity: 0.75, fontSize: 13 }}>
            Time: {timeLabel}
          </div>
        ) : null}

        {resultLabel ? (
          <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13 }}>
            Result: <b>{resultLabel}</b>
          </div>
        ) : null}

        <div style={{ height: 1, background: "#e6e6e6", marginTop: 10 }} />
      </div>
    );
  };

  return (
    <div className="accPage">
      {/* 4 Stat Cards */}
      <div className="accGrid4 accDashCards">
        <StatCard title="Subscription" value={subscriptionText} small />
        <StatCard title="Search history" value={historyCount} />
        <StatCard title="Saved birthdays" value={birthdaysCount} />
        <StatCard title="Orders" value="0" />
      </div>

      {/* Recent results */}
      <div className="accPanel accDashPanel" style={{ marginTop: 14 }}>
        <div className="accPageTitle" style={{ marginBottom: 10 }}>
          Recent results
        </div>

        {loadingLatest ? (
          <div className="accHelpText">Loading…</div>
        ) : latest ? (
          <div style={{ opacity: 1 }}>
            <div style={{ height: 1, background: "#e6e6e6", margin: "10px 0" }} />

            <div className="accHelpText" style={{ margin: 0 }}>
              <div style={{ fontWeight: 800 }}>
                <span style={{ opacity: 0.85 }}>Birthday searched:</span>{" "}
                <Link
                  to={latestUrl}
                  style={{
                    fontWeight: 900,
                    textDecoration: "underline",
                    color: "inherit",
                  }}
                >
                  {prettyDate || "View"}
                </Link>
              </div>

              {prettyTime ? (
                <div style={{ marginTop: 6, opacity: 0.75 }}>
                  Time: {prettyTime}
                </div>
              ) : null}

              {prettyResult ? (
                <div style={{ marginTop: 6, opacity: 0.85 }}>
                  Result: <b>{prettyResult}</b>
                </div>
              ) : null}
            </div>

            <div style={{ height: 1, background: "#e6e6e6", margin: "10px 0 0" }} />
          </div>
        ) : (
          <div className="accHelpText" style={{ opacity: 1 }}>
            No birthdays searched.{" "}
            <Link
              to="/"
              style={{
                fontWeight: 900,
                textDecoration: "underline",
                color: "inherit",
              }}
            >
              Start a new search
            </Link>
          </div>
        )}
      </div>

      {/* Previous results (same title style as recent results) */}
      <div className="accPanel accDashPanel" style={{ marginTop: 14 }}>
        <div className="accPageTitle" style={{ marginBottom: 10 }}>
          Recent results
        </div>

        {loadingHistory ? (
          <div className="accHelpText">Loading…</div>
        ) : previousItems.length ? (
          <div style={{ opacity: 1 }}>
            <div style={{ height: 1, background: "#e6e6e6" }} />
            {previousItems.slice(0, 10).map(renderRow)}
          </div>
        ) : (
          <div className="accHelpText" style={{ opacity: 1 }}>
            No previous results yet.
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, small = false }) {
  return (
    <div className="accStatCard">
      <div className="accStatTitle">{title}</div>
      <div className={`accStatValue ${small ? "is-small" : ""}`}>{value}</div>
    </div>
  );
}